import { Box, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const statusToLabel = (status) => {
    const map = {
        RINGING: 'A tocar',
        ONGOING: 'Em curso',
        ENDED: 'Terminada',
        REJECTED: 'Rejeitada',
        IGNORED: 'Não atendida',
        UNKNOWN: 'Desconhecida'
    };
    return map[status] || status || 'Desconhecida';
};

const statusToTone = (status) => {
    if (status === 'REJECTED' || status === 'IGNORED') {
        return {
            bgcolor: '#f5c4c4',
            color: '#8f2a2a'
        };
    }

    if (status === 'ONGOING') {
        return {
            bgcolor: '#d8f0dc',
            color: '#246334'
        };
    }

    return {
        bgcolor: '#e0e0e0',
        color: '#4c4c4c'
    };
};

const MissedCallsPanel = ({
    title,
    rows,
    footerText = 'Ver mais',
    onVideoCall,
    onPrevPage,
    onNextPage,
    disablePrev = false,
    disableNext = false,
    pageLabel = ''
}) => {
    return (
        <Paper
            variant="elevation=0"
            sx={{
                p: { xs: 1.8, md: 2.2 },
                bgcolor: '#ffffff',
                borderRadius: '20px',
                boxShadow: 'none'
            }}
        >
            <Typography variant="h6" fontWeight={700} mb={1.8} color="#3D3D3D">
                {title}
            </Typography>

            <Paper
                sx={{
                    px: 2.2,
                    py: 1.1,
                    mb: 1.1,
                    display: 'grid',
                    gridTemplateColumns: '22px 1.35fr 40px 1.15fr 1fr auto',
                    gap: 1.2,
                    bgcolor: '#3D3D3D',
                    color: '#fff',
                    borderRadius: '10px',
                    boxShadow: 'none'
                }}
            >
                <Box />
                <Typography variant="caption" fontWeight={600}>
                    Nome
                </Typography>
                <Box />
                <Typography variant="caption" fontWeight={600}>
                    Data
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                    Estado
                </Typography>
                <Typography variant="caption" fontWeight={600} textAlign="center">
                    Ação
                </Typography>
            </Paper>

            <Stack spacing={1}>
                {rows.map((row) => (
                    <Paper
                        key={row.id}
                        sx={{
                            px: 2.2,
                            py: 1.35,
                            display: 'grid',
                            gridTemplateColumns: '22px 1.35fr 40px 1.15fr 1fr auto',
                            alignItems: 'center',
                            gap: 1.2,
                            bgcolor: '#ECECEC',
                            borderRadius: '12px',
                            boxShadow: 'none'
                        }}
                    >
                        <FiberManualRecordIcon
                            sx={{
                                fontSize: 14,
                                color: row.isOnline ? '#26a65b' : '#d64541'
                            }}
                        />
                        <Typography variant="body2" fontWeight={600} color="#3D3D3D">
                            {row.name}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            {row.direction === 'outgoing' ? (
                                <CallMadeIcon fontSize="small" sx={{ color: '#D63731' }} />
                            ) : (
                                <CallReceivedIcon fontSize="small" sx={{ color: '#D63731' }} />
                            )}
                        </Box>
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                            {row.date}
                        </Typography>
                        <Stack direction="row" spacing={0.4} justifyContent="flex-start">
                            <Chip
                                size="small"
                                label={row.statusLabel || statusToLabel(row.status)}
                                sx={{
                                    minWidth: 70,
                                    height: 22,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    justifyContent: 'center',
                                    '& .MuiChip-label': {
                                        width: '100%',
                                        textAlign: 'center',
                                        px: 1
                                    },
                                    ...statusToTone(row.status)
                                }}
                            />
                        </Stack>
                        <Tooltip title={row.isOnline ? 'Iniciar videochamada' : 'Comecar videochamada'}>
                            <IconButton
                                size="small"
                                sx={{
                                    bgcolor: row.isOnline ? '#E91C24' : '#dd8a8d',
                                    color: '#fff',
                                    width: 34,
                                    height: 34,
                                    border: row.isOnline ? 'none' : '1.5px solid #D9D9D9',
                                    '&:hover': {
                                        bgcolor: '#ddb4b5'
                                    }
                                }}
                                onClick={() => onVideoCall?.(row)}
                            >
                                <VideocamOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Paper>
                ))}
            </Stack>

            {onPrevPage || onNextPage ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, gap: 0.5 }}>
                    <Tooltip title="Página anterior">
                        <span>
                            <IconButton size="small" onClick={onPrevPage} disabled={disablePrev}>
                                <KeyboardArrowLeftIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 28, textAlign: 'center', alignSelf: 'center' }}
                    >
                        {pageLabel}
                    </Typography>
                    <Tooltip title="Página seguinte">
                        <span>
                            <IconButton size="small" onClick={onNextPage} disabled={disableNext}>
                                <KeyboardArrowRightIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        {footerText}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default MissedCallsPanel;
